import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './quantum-pay.css';
import SelfieCapture from './components/SelfieCapture';
import NotificationPopup from './components/NotificationPopup';
import ChatWidget from './components/ChatWidget';
import KamelotApp from './QuantumPayApp';
import { notificationService } from './services/notificationService';

// Lazy load components for code splitting
const PaymentSuccessHandler = lazy(() => import('./components/PaymentSuccessHandler'));
const PaymentCancel = lazy(() => import('./components/PaymentCancel'));
const QuoteAccept = lazy(() => import('./components/QuoteAccept'));
const Chat = lazy(() => import('./components/Chat'));

// Loading fallback component
const AppLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3d 50%, #0a0e27 100%)',
    color: '#00d9ff',
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '1.2rem'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '50px',
        height: '50px',
        border: '3px solid rgba(0, 217, 255, 0.2)',
        borderTop: '3px solid #00d9ff',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 20px'
      }}></div>
      <div>Loading App...</div>
    </div>
  </div>
);

function App() {
  const [testNotif, setTestNotif] = useState(null);
  const [wsStatus, setWsStatus] = useState('Connecting...');
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  useEffect(() => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
    const wsHost = window.location.hostname || 'localhost';
    const wsUrl = wsProtocol + wsHost + '/ws';
    
    const connect = () => {
      const finalWsUrl = wsUrl;
      console.log('[WS] Connecting to', finalWsUrl);
      setWsStatus('Connecting...');
      
      try {
        wsRef.current = new WebSocket(finalWsUrl);
        
        wsRef.current.onopen = () => {
          console.log('[WS] Connected!');
          setWsStatus('Connected');
        };
        
        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('[WS] Received:', data);
            
            let notif = null;
            if (data.type === 'push_notification') {
              notif = data.data;
            } else if (data.type === 'notification_created') {
              notif = data.data;
            } else if (data.type === 'notification') {
              notif = data;
            }
            
            if (notif) {
              const title = notif.title || notif.body || 'Notification';
              const message = notif.body || notif.message || '';
              console.log('[WS] ======== RECEIVED NOTIFICATION ========');
              console.log('Title:', title, 'Message:', message);
              window.alert('INCOMING NOTIFICATION: ' + title + '\n\n' + message);
              setTestNotif({
                show: true,
                title: title,
                message: message,
                image: notif.image || null,
                timestamp: data.timestamp || new Date().toISOString()
              });
              setTimeout(() => setTestNotif(null), 8000);
            }
          } catch (e) {
            console.log('[WS] Parse error:', e);
          }
        };
        
        wsRef.current.onclose = () => {
          console.log('[WS] Disconnected, reconnecting in 3s...');
          setWsStatus('Disconnected - reconnecting');
          if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
          }
          reconnectTimeoutRef.current = setTimeout(connect, 3000);
        };
        
        wsRef.current.onerror = (err) => {
          console.log('[WS] Error:', err);
          setWsStatus('Error');
        };
      } catch (e) {
        console.log('[WS] Connection error:', e);
        setWsStatus('Error - retrying');
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      }
    };
    
    connect();
    
    const listener = (notif) => {
      console.log('[App] Notification received from service:', notif);
      setTestNotif({
        show: true,
        title: notif.title || notif.body || 'Notification',
        message: notif.body || notif.message || '',
        image: notif.image || null,
        timestamp: new Date().toISOString()
      });
      setTimeout(() => setTestNotif(null), 8000);
    };
    notificationService.init();
    notificationService.addListener(listener);
    
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      notificationService.removeListener(listener);
    };
  }, []);
  
  
  return (
    <Router future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }}>
      <div className="App">
        {/* WebSocket status indicator - remove after testing */}
        <div style={{position:'fixed',top:0,left:0,background:wsStatus==='Connected'?'green':'red',color:'white',padding:'5px 10px',zIndex:99999,fontSize:'12px'}}>
          WS: {wsStatus}
        </div>
        <ChatWidget />
        <NotificationPopup 
          show={testNotif?.show} 
          title={testNotif?.title} 
          message={testNotif?.message}
          timestamp={testNotif?.timestamp}
          onClose={() => setTestNotif(null)}
        />
        <SelfieCapture />
        <Suspense fallback={<AppLoader />}>
          <Routes>
            <Route path="/" element={<KamelotApp />} />
            <Route path="/payment/success" element={<PaymentSuccessHandler />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/quote-accept" element={<QuoteAccept />} />
            <Route path="/quote-accept.html" element={<QuoteAccept />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/chat.html" element={<Chat />} />
            <Route path="/chat-recover" element={<Chat />} />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
